import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

import API_BASE from '../config';
import { creditAwareFetch } from '../hooks/creditAwareFetch';

const SIGNAL_LABELS = {
  funding: 'Funding',
  leadership_change: 'Leadership',
  hiring: 'Hiring',
  tech_change: 'Tech',
  news: 'News'
};

const SIGNAL_COLOURS = {
  funding: '#276749',
  leadership_change: '#2B6CB0',
  hiring: '#B7791F',
  tech_change: '#6B46C1',
  news: '#888'
};

const SIGNAL_BG = {
  funding: '#F0FFF4',
  leadership_change: '#EBF8FF',
  hiring: '#FFFAF0',
  tech_change: '#FAF5FF',
  news: '#F7F7F7'
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function relativeTime(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 30) return formatDate(d);
  if (days > 0) return days + ' day' + (days === 1 ? '' : 's') + ' ago';
  if (hours > 0) return hours + ' hour' + (hours === 1 ? '' : 's') + ' ago';
  if (mins > 0) return mins + ' min' + (mins === 1 ? '' : 's') + ' ago';
  return 'just now';
}

export default function CompanyDetailView({ companyId, clientId, onSweepComplete, initialTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [selectedDigestId, setSelectedDigestId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedDigest, setEditedDigest] = useState('');
  const [saving, setSaving] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [sweepPrompt, setSweepPrompt] = useState('');
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: '', to_name: '', from_name: '' });
  const [sending, setSending] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(API_BASE + '/companies/' + companyId + '/intelligence');
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setData(json);
      if (json.digests && json.digests.length > 0 && !selectedDigestId) {
        setSelectedDigestId(json.digests[0].id);
      }
    } catch (e) {
      toast.error('Could not load intelligence: ' + e.message);
    }
    setLoading(false);
  }, [companyId, selectedDigestId]);

  useEffect(() => {
    setSelectedDigestId(null);
    setActiveTab(initialTab || 'overview');
    setEditing(false);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, initialTab]);

  const selectedDigest = data?.digests?.find(d => d.id === selectedDigestId) || data?.digests?.[0];
  const digestText = selectedDigest ? (selectedDigest.edited_digest || selectedDigest.generated_digest || '') : '';

  async function handleRunSweep() {
    if (!companyId || sweeping) return;
    setSweeping(true);
    const toastId = toast.loading('Researching ' + (data?.company?.name || 'company') + '...');
    try {
      const res = await creditAwareFetch(API_BASE + '/clients/' + clientId + '/intelligence/sweep', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_ids: [companyId], prompt: sweepPrompt })
      });
      if (!res) { setSweeping(false); return; }
      if (!res.ok) {
        if (res.status === 499) {
          toast('Research stopped', { id: toastId, icon: '⏹️' });
          setSweeping(false);
          return;
        }
        throw new Error(await res.text());
      }
      const newDigest = await res.json();
      toast.success('New digest generated', { id: toastId });
      setSweepPrompt('');
      await loadData();
      if (newDigest && newDigest.id) setSelectedDigestId(newDigest.id);
      setActiveTab('digest');
      if (onSweepComplete) onSweepComplete(newDigest);
    } catch (e) {
      toast.error('Sweep failed: ' + e.message, { id: toastId });
    }
    setSweeping(false);
  }

  async function stopSweep() {
    try {
      await fetch(API_BASE + '/jobs/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'digest', client_id: parseInt(clientId) })
      });
      toast('Stopping research...', { icon: '⏹️' });
    } catch (_) {}
  }

  async function handleSaveEdit() {
    if (!selectedDigest) return;
    setSaving(true);
    try {
      const res = await fetch(API_BASE + '/intelligence/' + selectedDigest.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edited_digest: editedDigest })
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Digest saved');
      setEditing(false);
      await loadData();
    } catch (e) {
      toast.error('Could not save: ' + e.message);
    }
    setSaving(false);
  }

  async function handleSendDigest() {
    if (!sendForm.to_email || !selectedDigest) return;
    setSending(true);
    const toastId = toast.loading('Sending digest to ' + sendForm.to_email + '...');
    try {
      const res = await fetch(API_BASE + '/intelligence/' + selectedDigest.id + '/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sendForm)
      });
      if (!res.ok) {
        const errBody = await res.text();
        // Log technical details to console for debugging
        console.error('[send-digest] ' + res.status + ': ' + errBody);
        // Show friendly message based on status
        let userMessage = 'Could not send digest. Please try again or contact support.';
        if (res.status === 401 || res.status === 403) {
          userMessage = 'Email service unavailable. Please contact support.';
        } else if (res.status === 422 || res.status === 400) {
          userMessage = 'Invalid email address. Please check and try again.';
        } else if (res.status >= 500) {
          userMessage = 'Email service is temporarily unavailable. Please try again in a moment.';
        }
        throw new Error(userMessage);
      }
      toast.success('Digest sent to ' + sendForm.to_email, { id: toastId });
      setShowSendForm(false);
      await loadData();
    } catch (e) {
      toast.error(e.message, { id: toastId });
    }
    setSending(false);
  }

  if (!companyId) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', fontSize: '0.9rem', padding: '3rem' }}>
        Select a company from the left to view its intelligence
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', fontSize: '0.9rem' }}>
        Loading...
      </div>
    );
  }

  if (!data) return null;

  const { company, digests = [], signals = [], sweep_meta = {} } = data;
  const sweepStatus = sweep_meta.sweep_status || 'never';
  const statusColour = sweepStatus === 'fresh' ? '#276749' : sweepStatus === 'due' ? '#B7791F' : '#888';
  const statusLabel = sweepStatus === 'fresh' ? 'Up to date' : sweepStatus === 'due' ? 'Due for refresh' : 'Never researched';

  // Build sources list (deduplicated URLs from signals)
  const sources = [];
  const seenUrls = new Set();
  signals.forEach(s => {
    if (s.source_url && !seenUrls.has(s.source_url)) {
      seenUrls.add(s.source_url);
      sources.push({
        url: s.source_url,
        source: s.source,
        signal_type: s.signal_type,
        date: s.detected_at
      });
    }
  });

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'digest', label: 'Digest' + (digests.length > 0 ? ' (' + digests.length + ')' : '') },
    { key: 'signals', label: 'Signals' + (signals.length > 0 ? ' (' + signals.length + ')' : '') },
    { key: 'sources', label: 'Sources' + (sources.length > 0 ? ' (' + sources.length + ')' : '') }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '0.5px solid #E8E4E0', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.3rem', fontWeight: '600', color: '#8B1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.name}</h2>
            <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: company.description ? '0.4rem' : 0 }}>
              {company.size && <span style={{ fontSize: '0.78rem', color: '#666' }}>{company.size}</span>}
              {company.location && <span style={{ fontSize: '0.78rem', color: '#666' }}>{company.size ? '· ' : ''}{company.location}</span>}
              {company.sector && <span style={{ fontSize: '0.78rem', color: '#666' }}>{(company.size || company.location) ? '· ' : ''}{company.sector}</span>}
              {company.website && <a href={company.website.startsWith('http') ? company.website : 'https://' + company.website} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', color: '#2B6CB0', textDecoration: 'none' }}>Website ↗</a>}
              {company.linkedin_url && <a href={company.linkedin_url.startsWith('http') ? company.linkedin_url : 'https://' + company.linkedin_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', color: '#2B6CB0', textDecoration: 'none' }}>LinkedIn ↗</a>}
            </div>
            {company.description && (
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#444', lineHeight: '1.5', maxWidth: '60ch' }}>{company.description}</p>
            )}
          </div>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.6rem', backgroundColor: statusColour + '15', color: statusColour, borderRadius: '12px', fontSize: '0.72rem', fontWeight: '600' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: statusColour }}></span>
              {statusLabel}
            </span>
            <button onClick={handleRunSweep} disabled={sweeping}
              style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: sweeping ? 'not-allowed' : 'pointer', opacity: sweeping ? 0.6 : 1, fontWeight: '500' }}>
              {sweeping ? 'Researching...' : digests.length > 0 ? 'Refresh intelligence' : 'Research now'}
            </button>
            {sweeping && (
              <button onClick={stopSweep}
                style={{ fontSize: '0.78rem', color: '#fff', background: '#B7791F', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500' }}>
                Stop research
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1.25rem', marginTop: '1rem', borderBottom: 'none' }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: '0.5rem 0', background: 'none', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #8B1A1A' : '2px solid transparent', color: activeTab === tab.key ? '#8B1A1A' : '#888', fontSize: '0.85rem', cursor: 'pointer', fontWeight: activeTab === tab.key ? '600' : '400', marginBottom: '-1px' }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
              <StatCard label="Digests generated" value={digests.length} />
              <StatCard label="Signals detected" value={signals.length} />
              <StatCard label="Last researched" value={sweep_meta.last_swept_at ? relativeTime(sweep_meta.last_swept_at) : 'Never'} small />
            </div>

            {digests.length > 0 && (
              <div>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '600' }}>Latest digest</p>
                <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '1rem', cursor: 'pointer' }} onClick={() => setActiveTab('digest')}>
                  <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', color: '#888' }}>{formatDate(digests[0].created_at)}</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#1A1A1A', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {(digests[0].edited_digest || digests[0].generated_digest || '').substring(0, 400)}
                  </p>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#8B1A1A', fontWeight: '500' }}>Read full digest →</p>
                </div>
              </div>
            )}

            {signals.length > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '600' }}>Recent signals</p>
                  <button onClick={() => setActiveTab('signals')} style={{ fontSize: '0.72rem', color: '#8B1A1A', background: 'none', border: 'none', cursor: 'pointer' }}>View all →</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {signals.slice(0, 3).map(s => <SignalCard key={s.id} signal={s} />)}
                </div>
              </div>
            )}

            {digests.length === 0 && signals.length === 0 && (
              <EmptyState
                title="No intelligence yet"
                message="Click 'Research now' to generate the first AI digest for this company."
              />
            )}
          </div>
        )}

        {/* DIGEST TAB */}
        {activeTab === 'digest' && (
          <div>
            {digests.length === 0 ? (
              <EmptyState
                title="No digest yet"
                message="Generate the first AI digest by clicking 'Research now' above. The AI will research recent signals, news, hiring, and changes for this company."
              />
            ) : (
              <>
                {/* Digest history dropdown */}
                {digests.length > 1 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.3rem', fontWeight: '500' }}>Viewing digest</label>
                    <select value={selectedDigestId || ''} onChange={e => { setSelectedDigestId(parseInt(e.target.value)); setEditing(false); }}
                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem', border: '1px solid #D4CFC9', borderRadius: '6px', backgroundColor: '#fff', minWidth: '300px' }}>
                      {digests.map(d => (
                        <option key={d.id} value={d.id}>{formatDate(d.created_at)}{d.id === digests[0].id ? ' (latest)' : ''}{d.sent_status === 'sent' ? ' · sent' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Action bar */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {!editing ? (
                    <>
                      <button onClick={() => { setEditedDigest(digestText); setEditing(true); }}
                        style={{ fontSize: '0.78rem', color: '#1A1A1A', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button onClick={() => setShowSendForm(true)}
                        style={{ fontSize: '0.78rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500' }}>
                        {selectedDigest?.sent_status === 'sent' ? 'Send again' : 'Send to client'}
                      </button>
                      {selectedDigest?.sent_status === 'sent' && (
                        <span style={{ fontSize: '0.72rem', color: '#276749', fontWeight: '500' }}>✓ Sent</span>
                      )}
                    </>
                  ) : (
                    <>
                      <button onClick={handleSaveEdit} disabled={saving}
                        style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', opacity: saving ? 0.6 : 1, fontWeight: '500' }}>
                        {saving ? 'Saving...' : 'Save changes'}
                      </button>
                      <button onClick={() => setEditing(false)}
                        style={{ fontSize: '0.78rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>

                {/* Send form */}
                {showSendForm && (
                  <div style={{ backgroundColor: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#276749' }}>Send digest to client</p>
                    <input type="email" placeholder="Client email" value={sendForm.to_email} onChange={e => setSendForm(p => ({ ...p, to_email: e.target.value }))} style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} />
                    <input type="text" placeholder="Recipient name (optional)" value={sendForm.to_name} onChange={e => setSendForm(p => ({ ...p, to_name: e.target.value }))} style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} />
                    <input type="text" placeholder="Your name (optional)" value={sendForm.from_name} onChange={e => setSendForm(p => ({ ...p, from_name: e.target.value }))} style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: '1px solid #D4CFC9', borderRadius: '6px' }} />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={handleSendDigest} disabled={sending || !sendForm.to_email}
                        style={{ fontSize: '0.78rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', opacity: (sending || !sendForm.to_email) ? 0.6 : 1, fontWeight: '500' }}>
                        {sending ? 'Sending...' : 'Send'}
                      </button>
                      <button onClick={() => setShowSendForm(false)}
                        style={{ fontSize: '0.78rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Digest body */}
                {editing ? (
                  <textarea value={editedDigest} onChange={e => setEditedDigest(e.target.value)}
                    style={{ width: '100%', minHeight: '400px', padding: '1rem', fontSize: '0.9rem', lineHeight: '1.6', border: '1px solid #D4CFC9', borderRadius: '8px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                ) : (
                  <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '1.25rem 1.5rem' }}>
                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.72rem', color: '#888' }}>Generated {formatDate(selectedDigest?.created_at)}</p>
                    <div style={{ fontSize: '0.9rem', lineHeight: '1.7', color: '#1A1A1A', whiteSpace: 'pre-wrap' }}>{digestText || 'Empty digest.'}</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* SIGNALS TAB */}
        {activeTab === 'signals' && (
          <div>
            {signals.length === 0 ? (
              <EmptyState title="No signals yet" message="Run a signal scan from Prospects, or wait for the daily sweep to find new signals." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {signals.map(s => <SignalCard key={s.id} signal={s} expanded />)}
              </div>
            )}
          </div>
        )}

        {/* SOURCES TAB */}
        {activeTab === 'sources' && (
          <div>
            {sources.length === 0 ? (
              <EmptyState title="No sources yet" message="Sources appear here as the AI gathers intelligence from across the web." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {sources.map((src, i) => (
                  <a key={i} href={src.url} target="_blank" rel="noreferrer"
                    style={{ display: 'block', padding: '0.75rem 1rem', backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '6px', textDecoration: 'none', color: '#1A1A1A' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
                      <span style={{ fontSize: '0.72rem', color: SIGNAL_COLOURS[src.signal_type] || '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{SIGNAL_LABELS[src.signal_type] || src.signal_type}</span>
                      <span style={{ fontSize: '0.72rem', color: '#888' }}>{formatDate(src.date)}</span>
                    </div>
                    <p style={{ margin: '0 0 0.2rem', fontSize: '0.85rem', fontWeight: '500', color: '#2B6CB0' }}>{src.source}</p>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{src.url}</p>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, small }) {
  return (
    <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.75rem 1rem' }}>
      <p style={{ margin: '0 0 0.2rem', fontSize: '0.68rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '600' }}>{label}</p>
      <p style={{ margin: 0, fontSize: small ? '0.95rem' : '1.4rem', fontWeight: '600', color: '#1A1A1A' }}>{value}</p>
    </div>
  );
}

function SignalCard({ signal, expanded }) {
  const colour = SIGNAL_COLOURS[signal.signal_type] || '#888';
  const bg = SIGNAL_BG[signal.signal_type] || '#F7F7F7';
  return (
    <div style={{ backgroundColor: bg, borderRadius: '8px', padding: '0.85rem 1rem', borderLeft: '3px solid ' + colour }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.68rem', fontWeight: '700', color: colour, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{SIGNAL_LABELS[signal.signal_type] || signal.signal_type}</span>
        <span style={{ fontSize: '0.7rem', color: '#888' }}>{formatDate(signal.detected_at)}</span>
      </div>
      <p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', color: '#1A1A1A', lineHeight: '1.5', fontWeight: '500' }}>{signal.signal_data?.summary}</p>
      {signal.source_url && (
        <a href={signal.source_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.72rem', color: '#2B6CB0', textDecoration: 'none' }}>{signal.source} ↗</a>
      )}
      {expanded && signal.relevance_score && (
        <span style={{ fontSize: '0.7rem', color: colour, fontWeight: '600', marginLeft: '0.6rem' }}>Score {signal.relevance_score}/10</span>
      )}
    </div>
  );
}

function EmptyState({ title, message }) {
  return (
    <div style={{ backgroundColor: '#FAFAF8', border: '0.5px dashed #E8E4E0', borderRadius: '8px', padding: '2.5rem 1.5rem', textAlign: 'center' }}>
      <p style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: '600', color: '#666' }}>{title}</p>
      <p style={{ margin: 0, fontSize: '0.85rem', color: '#888', lineHeight: '1.5' }}>{message}</p>
    </div>
  );
}
